import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, MessageSquare, Mail, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "../../components/auth/AuthLayout";
import { ResendCountdown } from "../../components/auth/ResendCountdown";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth, homeForRole } from "../../context/AuthContext";
import { authApi, publicApi, errorMessage } from "../../lib/api";
import { withIndiaPrefix } from "../../lib/smartDefaults";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isPhone = (v) => /^\+?[0-9][0-9\s-]{7,15}$/.test(v.trim());
const normalizePhone = (v) => {
  const digits = v.replace(/[^0-9+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.length === 10) return `+91${digits}`;
  return `+${digits}`;
};

export default function PhoneAuth() {
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginOnly = location.state?.mode === "login";
  const [identifier, setIdentifier] = useState("");
  const channel = "sms";
  const [request, setRequest] = useState(null); // { request_id, demo_otp, mock, message }
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("rider");
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null); // needs_profile payload when logging in with an unknown id
  const [extra, setExtra] = useState(""); // the other (unverified) contact on complete-profile
  const [address, setAddress] = useState("");
  const [channels, setChannels] = useState(null); // { email, sms, whatsapp, dev_mock }

  useEffect(() => {
    publicApi.notificationsStatus().then((st) => setChannels(st)).catch(() => setChannels(null));
  }, []);

  const channelLive = (c) => (channels ? !!channels[c] : null);

  const kind = useMemo(() => (isEmail(identifier) ? "email" : isPhone(identifier) ? "phone" : null), [identifier]);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (kind !== "email" && kind !== "phone") return toast.error("Enter a valid email or mobile number");
    if (kind === "phone" && channelLive("whatsapp") === false) return toast.error("WhatsApp codes aren't available right now — please use your email address.");
    setBusy(true);
    try {
      const body = kind === "phone"
        ? { phone: normalizePhone(identifier), channel: "whatsapp" }
        : { email: identifier.trim(), channel: "email" };
      const res = await authApi.otpRequest(body);
      setRequest({ ...res, kind, target: kind === "phone" ? body.phone : body.email });
      setOtp("");
      toast.success(kind === "phone" ? "Code sent on WhatsApp" : "Code sent to your email");
    } catch (err) {
      toast.error(errorMessage(err, "Could not send code"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.length < 4) return toast.error("Enter the code you received");
    setBusy(true);
    try {
      const body = { request_id: request.request_id, otp, login_only: true };
      if (request.kind === "email") body.email = request.target; else body.phone = request.target;
      const data = await authApi.otpVerify(body);
      if (data.needs_profile) {
        setProfile(data);
        setName("");
        setExtra("");
        setAddress("");
        return;
      }
      const user = applyAuth(data);
      toast.success(`Welcome, ${user.name}`);
      navigate(homeForRole(user.role), { replace: true });
    } catch (err) {
      toast.error(errorMessage(err, "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  const autoVerifyRef = useRef("");
  useEffect(() => {
    if (request && !profile && otp.length === 6 && !busy && autoVerifyRef.current !== otp) { autoVerifyRef.current = otp; verify(); }
  }, [otp, request, profile, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name");
    setBusy(true);
    try {
      const body = { request_id: profile.request_id, name: name.trim(), role };
      if (address.trim()) body.address = address.trim();
      if (extra.trim()) {
        if (profile.phone) body.email = extra.trim();
        else body.phone = normalizePhone(extra);
      }
      const data = await authApi.otpCompleteProfile(body);
      applyAuth(data);
      const u = data.user || {};
      toast.success(`Welcome to KohimaTaxi, ${u.name}`);
      const needsKyc = u.role !== "rider" && u.verification_status !== "verified";
      navigate(needsKyc ? "/app/kyc" : homeForRole(u.role), { replace: true });
    } catch (err) {
      toast.error(errorMessage(err, "Could not create your account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={profile ? "Complete your profile" : request ? "Enter your code" : loginOnly ? "Log in with a one-time code" : "Continue with a code"}
      subtitle={profile ? "Just a few details to finish setting up your account." : request ? `We sent a one-time code to ${request.target}.` : "Enter your email or mobile number — we'll send a one-time code by WhatsApp or email. No password needed."}
      footer={loginOnly ? <>No account yet? <Link to="/auth/signup" className="font-semibold text-brand hover:underline">Create an account</Link></> : <>Prefer a password? <Link to="/auth/login" className="font-semibold text-brand hover:underline">Log in with email</Link></>}
    >
      {!request ? (
        <form onSubmit={requestOtp} className="space-y-5" data-testid="otp-request-form">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2"><Label htmlFor="identifier">Email or mobile number</Label><span className="text-[11px] text-ink-muted" data-testid="otp-prefix-hint">WhatsApp or email code</span></div>
            <Input id="identifier" required type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@example.com  or  +91 98765 43210" className="h-11" data-testid="otp-identifier" autoComplete="username" />
            {kind === "email" && (
              <p className="flex items-center gap-1.5 text-xs text-graphite" data-testid="otp-detected">
                <Mail className="h-3.5 w-3.5" /> We will send the code from no-reply@kohimataxi.com — check spam if it does not arrive.
              </p>
            )}
            {kind === "phone" && channelLive("whatsapp") !== false && (
              <p className="flex items-center gap-1.5 text-xs text-graphite" data-testid="otp-phone-whatsapp">
                <MessageSquare className="h-3.5 w-3.5" /> We'll send your code on WhatsApp to this number.
              </p>
            )}
            {kind === "phone" && channelLive("whatsapp") === false && (
              <p className="flex items-center gap-1.5 text-xs text-ink-muted" data-testid="otp-phone-disabled">
                <Info className="h-3.5 w-3.5" /> WhatsApp codes aren't available right now — please enter your email address instead.
              </p>
            )}
          </div>
          <Button type="submit" disabled={busy || !(kind === "email" || (kind === "phone" && channelLive("whatsapp") !== false))} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="otp-send">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send code
          </Button>
        </form>
      ) : profile ? (
        <form onSubmit={submitProfile} className="space-y-5" data-testid="complete-profile-form">
          <p className="rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-ink" data-testid="complete-profile-intro">
            You're almost there! We verified <strong>{profile.email || profile.phone}</strong>. Tell us a bit about you to finish creating your account.
          </p>
          <div className="space-y-2">
            <Label htmlFor="cp-name">Full name</Label>
            <Input id="cp-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-11" data-testid="cp-name" />
          </div>
          <div className="space-y-2">
            <Label>I'm joining as</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-11" data-testid="cp-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rider">Customer</SelectItem>
                <SelectItem value="driver">Driver</SelectItem>
                <SelectItem value="restaurant">Restaurant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-extra">{profile.phone ? "Email address" : "Phone number"} <span className="text-ink-muted">(optional)</span></Label>
            <Input id="cp-extra" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder={profile.phone ? "you@example.com" : "+91 98765 43210"} className="h-11" data-testid="cp-extra" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-address">Address / location <span className="text-ink-muted">(optional)</span></Label>
            <Input id="cp-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Where should we reach you?" className="h-11" data-testid="cp-address" />
          </div>
          {role !== "rider" && <p className="text-xs text-ink-muted" data-testid="cp-kyc-note">You'll be asked to complete a quick verification (KYC) after this.</p>}
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="cp-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account &amp; continue
          </Button>
          <button type="button" onClick={() => { setProfile(null); setRequest(null); }} className="inline-flex items-center text-sm text-ink-soft hover:text-ink" data-testid="cp-back"><ArrowLeft className="mr-1 h-4 w-4" /> Start over</button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-5" data-testid="otp-verify-form">
          {request.mock && request.demo_otp ? (
            <div className="flex items-start gap-2 rounded-xl border border-graphite-200 bg-graphite-50 p-3 text-sm text-graphite-700" data-testid="otp-demo-hint">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This channel is in test mode. Use code <strong>{request.demo_otp}</strong>.</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-xl border border-surface-300 bg-surface-100 p-3 text-sm text-ink-soft" data-testid="otp-sent-hint">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{request.message || "Code sent."} Check your {request.kind === "email" ? "inbox" : "WhatsApp"}. It may take up to a minute.</span>
            </div>
          )}
          {request.kind === "email" && (
            <p className="flex items-center gap-1.5 text-xs text-ink-muted" data-testid="otp-spam-note">
              <Mail className="h-3.5 w-3.5" /> Not in your inbox? Check your spam or junk folder.
            </p>
          )}
          <div className="space-y-2">
            <Label>One-time code</Label>
            <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
              <InputOTPGroup className="gap-2">
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 rounded-xl border text-lg" />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="otp-verify">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {loginOnly ? "Verify and log in" : "Verify and continue"}
          </Button>
          <ResendCountdown onResend={requestOtp} busy={busy} nonce={request.request_id} testId="otp-resend" />
          <button type="button" onClick={() => setRequest(null)} className="inline-flex items-center text-sm text-ink-soft hover:text-ink" data-testid="otp-back"><ArrowLeft className="mr-1 h-4 w-4" /> Change number</button>
        </form>
      )}
    </AuthLayout>
  );
}
